import type { ViewMode } from '../lib/parse-view';
import type { SortKey, SortDir } from '../lib/parse-sort';
import type { GridCols } from '../lib/parse-grid-cols';
import { I } from '../lib/icons';

/**
 * ResultsToolbar — Präsentations-Controls (Sortierung + Ansicht) über den
 * Ergebnissen, rechtsbündig (2026-06-10, IA-Entscheidung). Vorher saßen sie
 * oben im Filter-Panel; Sort/Ansicht sind aber Präsentation, kein Filter
 * (im Code längst getrennt: Sort ist nicht in `hasActiveFilters` und wird beim
 * Filter-Reset NICHT gecleart). Gehören damit neben „N Assets"/Aktive-Chips,
 * nicht zwischen die Filter-Facetten. FilterBar = jetzt rein Filter-Setzen.
 */
type Props = {
  sort: SortKey;
  dir: SortDir;
  onSortChange: (key: SortKey, dir: SortDir) => void;
  view: ViewMode;
  onViewChange: (v: ViewMode) => void;
  /** Spalten-Regler (3–5) — nur in der Grid-Ansicht relevant. */
  gridCols: GridCols;
  onGridColsChange: (n: GridCols) => void;
  /** Stapel-Modus (`?stacks=1`): gleichnamige Assets als ein Stapel-Tile gruppieren. */
  stacks: boolean;
  onStacksChange: (v: boolean) => void;
};

// 4 cols × 2 dirs = 8 Sort-Options. Reihenfolge: häufigste Use-Cases zuerst.
// `value` ist `${sort}-${dir}` — composite-key nur fürs <select>-Binding.
const SORT_OPTIONS: { value: string; sort: SortKey; dir: SortDir; label: string }[] = [
  { value: 'recent-desc', sort: 'recent', dir: 'desc', label: 'Neueste zuerst' },
  { value: 'recent-asc', sort: 'recent', dir: 'asc', label: 'Älteste zuerst' },
  { value: 'name-asc', sort: 'name', dir: 'asc', label: 'Name A→Z' },
  { value: 'name-desc', sort: 'name', dir: 'desc', label: 'Name Z→A' },
  { value: 'size-desc', sort: 'size', dir: 'desc', label: 'Größte zuerst' },
  { value: 'size-asc', sort: 'size', dir: 'asc', label: 'Kleinste zuerst' },
  { value: 'type-asc', sort: 'type', dir: 'asc', label: 'Type A→Z' },
  { value: 'type-desc', sort: 'type', dir: 'desc', label: 'Type Z→A' },
];

// Redesign S51: „ghost" — Ruhezustand transparent (nur Text+Icon), Fill + Schatten
// erst beim Hover. Der Toolbar-Bereich verschwindet fast in die Seite, bis man
// interagiert.
const SORT_SELECT_CLASS =
  'h-9 px-3 rounded-md text-xs font-semibold bg-transparent text-brand-green-800 hover:bg-ink-100 hover:shadow-soft transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700';

export function ResultsToolbar({
  sort,
  dir,
  onSortChange,
  view,
  onViewChange,
  gridCols,
  onGridColsChange,
  stacks,
  onStacksChange,
}: Props) {
  return (
    // Zwei Cluster (2026-07-18): links „Inhalt & Reihenfolge" (Stapeln + Sort),
    // rechts „Layout-Geometrie" (Ansicht + Spalten), getrennt durch eine zarte
    // Hairline. Vorher standen die 4 Controls im Zickzack Inhalt/Layout/Inhalt/
    // Layout und die nackten Spalten-Zahlen „lose im Raum" ohne Anker.
    <div className="flex items-center gap-2 shrink-0">
      {/* ── Cluster 1: Inhalt & Reihenfolge ── */}
      {/* Stapel-Toggle: gleichnamige Assets (ohne Sequenz-Suffix) als ein Tile
          gruppieren. Asset-typ-neutral („Stapeln", nicht „Bilder"). */}
      <button
        type="button"
        onClick={() => onStacksChange(!stacks)}
        aria-pressed={stacks}
        title="Gleichnamige Assets stapeln"
        className={`h-9 px-3 inline-flex items-center gap-1.5 rounded-md text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 ${
          stacks
            ? 'bg-brand-green-700 text-brand-cream shadow-soft'
            : 'bg-transparent text-brand-green-800 hover:bg-ink-100 hover:shadow-soft'
        }`}
      >
        <I.stack size={15} />
        Stapeln
      </button>

      <select
        value={SORT_OPTIONS.find((o) => o.sort === sort && o.dir === dir)?.value ?? 'recent-desc'}
        onChange={(e) => {
          const opt = SORT_OPTIONS.find((o) => o.value === e.target.value);
          if (opt) onSortChange(opt.sort, opt.dir);
        }}
        aria-label="Sortierung"
        className={SORT_SELECT_CLASS}
      >
        {SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Cluster-Trennung: zarte Hairline. Zwischen zwei „ghost"-Controls (Sort ↔
          Ansicht, beide im Ruhezustand transparent) trägt Whitespace allein zu
          schwach — die Linie macht die Gruppierung eindeutig. */}
      <div aria-hidden="true" className="h-5 w-px bg-ink-200 mx-1" />

      {/* ── Cluster 2: Layout-Geometrie ── */}
      {/* Redesign S51: kein Pill-Container mehr — zwei schlichte Icon-Buttons.
          Aktiv = grün-getöntes Highlight (brand-tinted, klar vom grauen Hover
          unterscheidbar), inaktiv = graues Icon → grün beim Hover. */}
      <div role="group" aria-label="Ansicht" className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => onViewChange('grid')}
          aria-pressed={view === 'grid'}
          aria-label="Rasteransicht"
          title="Rasteransicht"
          className={`h-9 w-9 rounded-md transition flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 ${
            view === 'grid'
              ? 'bg-brand-green-100 text-brand-green-700'
              : 'text-ink-400 hover:bg-ink-100 hover:text-brand-green-700'
          }`}
        >
          <I.grid size={16} />
        </button>
        <button
          type="button"
          onClick={() => onViewChange('list')}
          aria-pressed={view === 'list'}
          aria-label="Listenansicht"
          title="Listenansicht"
          className={`h-9 w-9 rounded-md transition flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 ${
            view === 'list'
              ? 'bg-brand-green-100 text-brand-green-700'
              : 'text-ink-400 hover:bg-ink-100 hover:text-brand-green-700'
          }`}
        >
          <I.list size={16} />
        </button>
      </div>

      {/* Spalten-Pill: nur in Grid-Ansicht + nur auf lg+ (darunter ist die
          Spaltenzahl ohnehin fix 2/3, da wäre der Regler wirkungslos). Sitzt
          direkt neben dem View-Toggle (beide antworten „wie sieht das Grid
          aus"). Als EINZIGES Toolbar-Element mit persistentem Track — bewusste
          Ausnahme vom Ghost-Prinzip: die nackten Zahlen wirkten ohne Container
          „lose im Raum" + ohne Anker (2026-07-18). Das gemutete Columns-Glyph
          sagt WAS die Zahlen bedeuten, der Track macht sie zu EINEM Control. */}
      {view === 'grid' && (
        <div
          role="group"
          aria-label="Spalten"
          title={`Spalten: ${gridCols}`}
          className="hidden lg:inline-flex items-center gap-1 h-9 pl-2.5 pr-1 rounded-lg bg-ink-100"
        >
          <I.columns size={15} className="text-ink-400" aria-hidden="true" />
          {([3, 4, 5] as GridCols[]).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onGridColsChange(n)}
              aria-pressed={gridCols === n}
              aria-label={`${n} Spalten`}
              className={`h-7 w-7 rounded-md text-xs font-semibold tabular-nums transition flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 ${
                gridCols === n
                  ? 'bg-brand-green-100 text-brand-green-700 shadow-soft'
                  : 'text-ink-400 hover:text-brand-green-700'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
